import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Divider, Form, Input, InputNumber, Modal, Row, Select, Spin, Switch, Tag, message } from 'antd';
import { withRouter } from 'react-router-dom';
import { withAppContext } from 'context/AppContext';
import { buildEInvoicePlanDetailUri, buildEInvoicePlansUri } from 'utils/routes';
import { createProduct, deleteProduct, getProduct, reactivateProduct, updateProduct, UNLIMITED_EINVOICE_COUNT } from 'utils/apis/eInvoicePlan';
import { StyledCard, HeaderSection, BodySection } from './eInvoice.styles';
import CouponPageHeader from './components/CouponPageHeader';

const { Option } = Select;
const { TextArea } = Input;
const MAX_MARKETING_FEATURES = 15;

const NAVIGATION_KEYS = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'];

const blockDecimalKeyDown = e => {
  if (NAVIGATION_KEYS.indexOf(e.key) !== -1 || e.ctrlKey || e.metaKey) {
    return;
  }
  if (/^\d$/.test(e.key)) {
    return;
  }
  if (e.key === '.' && String(e.target.value).indexOf('.') === -1) {
    return;
  }
  e.preventDefault();
};

const parseDecimalInput = value => {
  if (value == null || value === '') {
    return '';
  }
  const cleaned = String(value).replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length <= 1) {
    return cleaned;
  }
  return `${parts[0]}.${parts.slice(1).join('')}`;
};

const blockIntegerKeyDown = e => {
  if (NAVIGATION_KEYS.indexOf(e.key) !== -1 || e.ctrlKey || e.metaKey) {
    return;
  }
  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

const parseIntegerInput = value => {
  if (value == null || value === '') {
    return '';
  }
  return String(value).replace(/[^\d]/g, '');
};

const decimalInputProps = {
  min: 0,
  step: 0.01,
  precision: 2,
  style: { width: '100%' },
  onKeyDown: blockDecimalKeyDown,
  parser: parseDecimalInput
};

const integerInputProps = {
  min: 0,
  precision: 0,
  style: { width: '100%' },
  onKeyDown: blockIntegerKeyDown,
  parser: parseIntegerInput
};

const FIELD_HELP = {
  name: 'Display name shown in checkout and admin.',
  description: 'Optional description for this plan.',
  currency: 'Currency for Stripe prices. Cannot change after creation.',
  pricePerMonth: 'Monthly subscription amount. Changing this on edit creates a new Stripe price.',
  pricePerYear: 'Optional annual subscription amount. Leave blank if not offered. Changing this on edit creates a new Stripe price.',
  active: 'Inactive plans are hidden from checkout.',
  unlimited: 'When enabled, quota is treated as unlimited.',
  eInvoiceCount: 'Number of e-Invoices included per billing period. Set here in the dashboard (synced to Stripe).',
  marketingFeatures: 'A list of product features that will be visible to customers. Displayed in pricing tables.'
};

const normalizeMarketingFeatures = values => {
  if (!Array.isArray(values)) {
    return [];
  }
  return values
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .slice(0, MAX_MARKETING_FEATURES);
};

const resolveMarketingFeaturesFromPlan = (loadedPlan, stripeProduct) => {
  if (loadedPlan.marketingFeatures && loadedPlan.marketingFeatures.length) {
    return loadedPlan.marketingFeatures;
  }
  if (stripeProduct && stripeProduct.marketing_features && stripeProduct.marketing_features.length) {
    return stripeProduct.marketing_features.map(feature => feature.name).filter(Boolean);
  }
  return [];
};

const PlanDetails = ({ form, match, history, user }) => {
  const id = match.params.id;
  const isEdit = Boolean(id);
  const { getFieldDecorator, getFieldValue, setFieldsValue, validateFieldsAndScroll } = form;

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [featureCount, setFeatureCount] = useState(1);

  const isActive = plan && plan.active !== false;

  const loadPlan = useCallback(() => {
    if (!isEdit || !id) {
      return Promise.resolve();
    }

    setLoading(true);
    return getProduct(id)
      .then(data => {
        const loadedPlan = data.product || data;
        const stripeProduct = data.stripeProduct;
        setPlan(loadedPlan);
        const unlimited = Number(loadedPlan.eInvoiceCount) >= UNLIMITED_EINVOICE_COUNT;
        const marketingFeatures = resolveMarketingFeaturesFromPlan(loadedPlan, stripeProduct);
        const formFeatures = marketingFeatures.length ? marketingFeatures : [''];
        setFeatureCount(formFeatures.length);
        setFieldsValue({
          name: loadedPlan.name,
          description: loadedPlan.description,
          currency: (loadedPlan.currency || 'myr').toLowerCase(),
          pricePerMonth: loadedPlan.pricePerMonth,
          pricePerYear: loadedPlan.pricePerYear,
          unlimited,
          eInvoiceCount: unlimited ? undefined : loadedPlan.eInvoiceCount,
          active: loadedPlan.active !== false,
          marketingFeatures: formFeatures
        });
      })
      .catch(() => message.error('Failed to load plan'))
      .finally(() => setLoading(false));
  }, [id, isEdit, setFieldsValue]);

  useEffect(() => {
    if (user && !user.isAdmin) {
      history.push('/forbidden');
    }
  }, [user, history]);

  useEffect(() => {
    if (!isEdit || !user || !user.isAdmin) return;
    loadPlan();
  }, [isEdit, user, loadPlan]);

  const handleDeactivate = () => {
    Modal.confirm({
      title: 'Deactivate plan?',
      content: 'This will hide the plan from checkout and deactivate it in Stripe.',
      okText: 'Deactivate',
      okType: 'danger',
      onOk: () =>
        deleteProduct(id)
          .then(() => {
            message.success('Plan deactivated');
            return loadPlan();
          })
          .catch(() => message.error('Failed to deactivate plan'))
    });
  };

  const handleReactivate = () => {
    Modal.confirm({
      title: 'Reactivate plan?',
      content: 'This will make the plan available in checkout again.',
      okText: 'Reactivate',
      onOk: () =>
        reactivateProduct(id)
          .then(() => {
            message.success('Plan reactivated');
            return loadPlan();
          })
          .catch(() => message.error('Failed to reactivate plan'))
    });
  };

  const handleAddFeature = () => {
    if (featureCount >= MAX_MARKETING_FEATURES) {
      return;
    }
    const currentFeatures = getFieldValue('marketingFeatures') || [];
    setFeatureCount(featureCount + 1);
    setFieldsValue({
      marketingFeatures: [...currentFeatures, '']
    });
  };

  const handleRemoveFeature = index => {
    const currentFeatures = [...(getFieldValue('marketingFeatures') || [])];
    currentFeatures.splice(index, 1);
    const nextFeatures = currentFeatures.length ? currentFeatures : [''];
    setFeatureCount(nextFeatures.length);
    setFieldsValue({ marketingFeatures: nextFeatures });
  };

  const pageTitle = isEdit ? (plan && plan.name) || 'Plan' : 'Create Plan';

  const headerActions =
    isEdit && plan ? (
      isActive ? (
        <Button type="danger" icon="stop" onClick={handleDeactivate}>
          Deactivate
        </Button>
      ) : (
        <Button type="primary" icon="check-circle" onClick={handleReactivate}>
          Reactivate
        </Button>
      )
    ) : null;

  if (!user || !user.isAdmin) {
    return null;
  }

  const handleSubmit = e => {
    e.preventDefault();
    validateFieldsAndScroll(async (err, values) => {
      if (err) return;

      setSubmitting(true);
      try {
        const marketingFeatures = normalizeMarketingFeatures(values.marketingFeatures);
        const payload = {
          name: values.name,
          description: values.description || '',
          currency: values.currency,
          pricePerMonth: values.pricePerMonth,
          pricePerYear: values.pricePerYear || null,
          unlimited: values.unlimited === true,
          eInvoiceCount: values.unlimited ? UNLIMITED_EINVOICE_COUNT : values.eInvoiceCount,
          active: values.active !== false,
          marketingFeatures
        };

        if (isEdit) {
          const updatePayload = {
            name: payload.name,
            description: payload.description,
            unlimited: payload.unlimited,
            eInvoiceCount: payload.eInvoiceCount,
            marketingFeatures
          };
          if (values.pricePerMonth != null) {
            updatePayload.pricePerMonth = values.pricePerMonth;
          }
          if (values.pricePerYear != null) {
            updatePayload.pricePerYear = values.pricePerYear;
          }
          await updateProduct(id, updatePayload);
          message.success('Plan updated');
          await loadPlan();
        } else {
          const created = await createProduct(payload);
          message.success('Plan created');
          history.push(buildEInvoicePlanDetailUri(String(created._id)));
        }
      } catch (error) {
        message.error((error && error.message) || 'Failed to save plan');
      } finally {
        setSubmitting(false);
      }
    });
  };

  const unlimited = getFieldValue('unlimited');

  return (
    <StyledCard bodyStyle={{ padding: 0 }}>
      <HeaderSection>
        <CouponPageHeader backTo={buildEInvoicePlansUri()} backLabel="Back to Plans" title={pageTitle} actions={headerActions} />
      </HeaderSection>
      <BodySection>
        <Spin spinning={loading}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
            message={
              isEdit
                ? 'Changing prices creates new Stripe prices and archives the old ones. Stripe product and price IDs cannot be edited directly.'
                : 'Plans are created in Stripe and mirrored locally. Price amounts cannot be changed in place after creation — edits create new Stripe prices.'
            }
          />

          <Form layout="vertical" onSubmit={handleSubmit}>
            {isEdit && (
              <Row gutter={16} style={{ marginBottom: 8 }}>
                <Col span={8}>
                  <Form.Item label="Status">{isActive ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}</Form.Item>
                </Col>
                {plan && plan.productId && (
                  <Col span={16}>
                    <Form.Item label="Stripe Product ID">
                      <Input disabled value={plan.productId} />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Name" help={FIELD_HELP.name}>
                  {getFieldDecorator('name', {
                    rules: [{ required: true, message: 'Name is required' }]
                  })(<Input placeholder="e.g. Pro" maxLength={255} />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Currency" help={FIELD_HELP.currency}>
                  {getFieldDecorator('currency', {
                    initialValue: 'myr',
                    rules: [{ required: !isEdit, message: 'Currency is required' }]
                  })(
                    <Select disabled={isEdit}>
                      <Option value="myr">MYR</Option>
                      <Option value="usd">USD</Option>
                      <Option value="sgd">SGD</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Description" help={FIELD_HELP.description}>
              {getFieldDecorator('description')(<TextArea rows={3} placeholder="Optional description" />)}
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Price / Month" help={FIELD_HELP.pricePerMonth}>
                  {getFieldDecorator('pricePerMonth', {
                    rules: [{ required: !isEdit, message: 'Monthly price is required' }]
                  })(<InputNumber {...decimalInputProps} />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Annual Price" help={FIELD_HELP.pricePerYear}>
                  {getFieldDecorator('pricePerYear')(<InputNumber {...decimalInputProps} />)}
                </Form.Item>
              </Col>
              {!isEdit && (
                <Col span={8}>
                  <Form.Item label="Active" help={FIELD_HELP.active}>
                    {getFieldDecorator('active', {
                      valuePropName: 'checked',
                      initialValue: true
                    })(<Switch />)}
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Unlimited e-Invoices" help={FIELD_HELP.unlimited}>
                  {getFieldDecorator('unlimited', {
                    valuePropName: 'checked',
                    initialValue: false
                  })(<Switch />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="e-Invoice Quota" help={FIELD_HELP.eInvoiceCount}>
                  {unlimited ? <Input disabled value="Unlimited" /> : null}
                  <div style={{ display: unlimited ? 'none' : undefined }}>
                    {getFieldDecorator('eInvoiceCount', {
                      rules: [{ required: !unlimited, message: 'e-Invoice quota is required' }]
                    })(<InputNumber {...integerInputProps} />)}
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Marketing Features</Divider>

            {Array.from({ length: featureCount }).map((_, index) => (
              <Form.Item
                key={`marketing-feature-${index}`}
                label={index === 0 ? 'Feature' : ' '}
                colon={index === 0}
                help={index === 0 ? FIELD_HELP.marketingFeatures : undefined}
              >
                <Row gutter={8} type="flex" align="middle">
                  <Col span={featureCount > 1 ? 20 : 24}>
                    {getFieldDecorator(`marketingFeatures[${index}]`, {
                      rules: [{ max: 80, message: 'Feature must be 80 characters or fewer' }]
                    })(<Input placeholder="e.g. 50 Invoices" maxLength={80} />)}
                  </Col>
                  {featureCount > 1 && (
                    <Col span={4}>
                      <Button type="danger" icon="delete" onClick={() => handleRemoveFeature(index)}>
                        Remove
                      </Button>
                    </Col>
                  )}
                </Row>
              </Form.Item>
            ))}

            {featureCount < MAX_MARKETING_FEATURES && (
              <Form.Item>
                <Button type="dashed" icon="plus" onClick={handleAddFeature}>
                  Add feature
                </Button>
              </Form.Item>
            )}

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {isEdit ? 'Save Changes' : 'Create Plan'}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </BodySection>
    </StyledCard>
  );
};

export default withAppContext(withRouter(Form.create()(PlanDetails)));
